/**
 * The referral ("invite") code a visitor arrived with, kept for the account
 * they may go on to open.
 *
 * WHY IT EXISTS. Staff hand out referral links, `supa.place/join/{code}`, made
 * in the admin console's Referral links tool. next.config.js redirects one to
 * /sell?invite={code}, and when the visitor opens an account the
 * claimReferral callable credits that account to the link, so the console can
 * show which links bring sellers in and what they list. A link may carry a
 * revenue share for whoever hands it out, worked out on the server from what
 * the account later earns supa.place (functions/src/lib/referrals.ts); nothing
 * here moves money or grants the account anything.
 *
 * Since 2026-10-19 a link can end in a slug that is not its code. Here the two
 * are the same thing: the browser keeps whichever the link ended in, and the
 * server finds the link from either (codes and slugs are one namespace).
 *
 * THE CODE CAN ALSO BE TYPED. The signup page has an optional "Referral code"
 * box (since 2026-10-15) for the same code, for someone who heard it in a video
 * or podcast, or followed the link on another device. A typed code wins over a
 * link followed earlier (chooseClaim), is checked as it is typed so a typo is
 * caught before the account exists (referralCodeFieldState and the
 * checkReferralCode callable), and is never stored: it goes with the claim.
 *
 * WHERE THE CODE IS KEPT. In the tab's memory, which carries it through every
 * in-app navigation from /sell to signup without storing anything. On the
 * device (localStorage `inviteLink`) ONLY while the visitor has accepted
 * analytics: remembering how someone arrived is not strictly necessary for
 * anything they asked for, so under the EU rules on device storage it waits
 * for that consent, and withdrawing it removes the code. Either way a code
 * lasts 30 days from the click, and the device copy is removed as soon as a
 * claim for an account opened with it has been answered. The key is disclosed
 * in the Privacy Policy's storage table (COOKIE_ROWS in
 * src/app/privacy-policy/page.tsx), and invite-link.test.ts fails if that row
 * goes, so rename both or neither.
 *
 * THE STORED VALUE IS UNTRUSTED: anything can write localStorage. It is
 * validated on every read, and one that fails is removed.
 *
 * Pure apart from what is handed to createInviteTracker, so the node tests
 * drive it with plain objects. The browser wiring is invite-link-client.ts.
 */

import { readStorageItem, removeStorageItems, writeStorageItem, type KeyValueStorage } from './browser-storage';

/** The query parameter the /join redirect puts the code in. */
export const INVITE_QUERY_PARAM = 'invite';

/** localStorage key for the kept code. Disclosed by name in the Privacy Policy. */
export const INVITE_STORAGE_KEY = 'inviteLink';

/** How long a kept code lasts, from when it was first saved. The Privacy Policy states it. */
export const INVITE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** The callables in functions/src/index.ts. invite-link.test.ts checks each is exported. */
export const CLAIM_REFERRAL_CALLABLE = 'claimReferral';
export const RECORD_REFERRAL_VISIT_CALLABLE = 'recordReferralVisit';
export const CHECK_REFERRAL_CODE_CALLABLE = 'checkReferralCode';

// MIRRORED from normalizeReferralSlug in functions/src/lib/referrals.ts, and
// invite-link.test.ts runs both over the same inputs: a code kept here must be
// one the server accepts.
const CODE_MIN_LENGTH = 3;
const CODE_MAX_LENGTH = 32;
const CODE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A code as the server compares it (trimmed, lowercased), or null when it is not a valid one. */
export function normalizeInviteCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toLowerCase();
  if (code.length < CODE_MIN_LENGTH || code.length > CODE_MAX_LENGTH) return null;
  return CODE_PATTERN.test(code) ? code : null;
}

/** The code in a page address's query string (`location.search`), or null. */
export function inviteCodeFromSearch(search: string): string | null {
  try {
    return normalizeInviteCode(new URLSearchParams(search).get(INVITE_QUERY_PARAM));
  } catch {
    return null;
  }
}

type KeptInvite = { code: string; savedAt: number };

/** The kept code, or null when there is none worth using: absent, malformed, expired, or dated in the future. */
function readKept(storage: KeyValueStorage | null, nowMs: number): KeptInvite | null {
  const raw = readStorageItem(storage, INVITE_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { code, savedAt } = parsed as { code?: unknown; savedAt?: unknown };
    // Exactly as it is written below, never merely close to it.
    if (typeof code !== 'string' || normalizeInviteCode(code) !== code) return null;
    if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;
    const age = nowMs - savedAt;
    return age >= 0 && age <= INVITE_TTL_MS ? { code, savedAt } : null;
  } catch {
    return null;
  }
}

export interface InviteTrackerDeps {
  /** localStorage in the browser; null where there is none to use. */
  storage: KeyValueStorage | null;
  /** Whether the visitor has accepted analytics right now. */
  consented: () => boolean;
  /** Report a visit to a link (the recordReferralVisit callable). Best-effort. */
  recordVisit: (code: string) => void;
  now: () => number;
}

export interface InviteTracker {
  /** A page load or navigation; `search` is `location.search`. */
  capture(search: string): void;
  /** The visitor accepted or withdrew analytics. */
  consentChanged(): void;
  /** The code to credit a newly created account to, or null. */
  codeForClaim(): string | null;
  /**
   * The server has answered a claim for an account opened here. The device
   * copy has done its job, so it is removed, and this tab does not store its
   * code again unless the link is opened again.
   */
  claimAnswered(): void;
}

/**
 * The per-tab keeper of the invite code.
 *
 * ONLY OPENING A LINK WRITES TO THE DEVICE. A page without ?invite= never
 * does, so a tab opened earlier cannot overwrite a newer link another tab
 * stored, and a tab left open cannot keep renewing a code: a code is dated
 * from the click, and at 30 days it is spent in the tab and on the device
 * alike. The one other write is accepting analytics after arriving, which
 * stores the tab's code (dated from its click) only when nothing usable is
 * stored already. Opening the link again stores it again only if the stored
 * copy is gone or is another link's, so revisiting does not stretch its 30 days.
 *
 * A visit is reported once per code per page lifetime; the server also counts
 * at most one a day per visitor, so reloads and later pages add nothing.
 */
export function createInviteTracker(deps: InviteTrackerDeps): InviteTracker {
  // The link this tab was opened through, when, and whether a claim has
  // since been answered for it.
  let tab: { code: string; at: number; claimed: boolean } | null = null;
  const reported = new Set<string>();

  const withinLifetime = (at: number, nowMs: number) => nowMs - at >= 0 && nowMs - at <= INVITE_TTL_MS;
  const store = (code: string, savedAt: number) =>
    writeStorageItem(deps.storage, INVITE_STORAGE_KEY, JSON.stringify({ code, savedAt }));
  const forget = () => removeStorageItems(deps.storage, INVITE_STORAGE_KEY);

  return {
    capture(search) {
      const nowMs = deps.now();
      const code = inviteCodeFromSearch(search);
      if (code) {
        tab = { code, at: nowMs, claimed: false };
        if (!reported.has(code)) {
          reported.add(code);
          try {
            deps.recordVisit(code);
          } catch {
            // A visit count is never worth an error on the page.
          }
        }
      }
      if (!deps.consented()) {
        forget();
        return;
      }
      const kept = readKept(deps.storage, nowMs);
      if (code && kept?.code !== code) store(code, nowMs);
      // Nothing usable is stored: clear a malformed or expired value.
      else if (!kept) forget();
    },
    consentChanged() {
      if (!deps.consented()) {
        forget();
        return;
      }
      const nowMs = deps.now();
      // Never over what is stored: that is another tab's link, at least as new.
      if (readKept(deps.storage, nowMs)) return;
      if (tab && !tab.claimed && withinLifetime(tab.at, nowMs)) store(tab.code, tab.at);
      else forget();
    },
    codeForClaim() {
      const nowMs = deps.now();
      if (tab && withinLifetime(tab.at, nowMs)) return tab.code;
      if (!deps.consented()) return null;
      return readKept(deps.storage, nowMs)?.code ?? null;
    },
    claimAnswered() {
      if (tab) tab.claimed = true;
      forget();
    },
  };
}

/** What a new account is credited with, and how: the server records `via` (readClaimMethod). */
export type ReferralClaim = { code: string; via: 'code' | 'link' };

/**
 * Which code to credit a just-created account with. A code typed into the
 * signup page's Referral code box wins: the visitor chose it, which a link
 * followed earlier (possibly someone else's) does not prove. Otherwise the
 * link this tab or device kept (InviteTracker.codeForClaim), or nothing.
 *
 * Anything typed that is not a code counts as nothing typed; the signup page
 * never lets one through (referralCodeFieldState holds the signup).
 */
export function chooseClaim(typed: string | null | undefined, tracked: string | null): ReferralClaim | null {
  const code = normalizeInviteCode(typed);
  if (code) return { code, via: 'code' };
  return tracked ? { code: tracked, via: 'link' } : null;
}

/**
 * What to claim next when the server answered a claim without crediting it:
 * a typed code that turned out not to be a live link's (a typo let through
 * while the check could not run, or a link switched off in between) falls back
 * to the link this tab or device kept, so it does not cost the affiliate whose
 * link was followed. Nothing else is retried: a credited claim is done, and a
 * refused link claim would be refused again.
 */
export function fallbackClaim(sent: ReferralClaim, tracked: string | null, claimed: boolean): ReferralClaim | null {
  if (claimed || sent.via !== 'code' || !tracked || tracked === sent.code) return null;
  return { code: tracked, via: 'link' };
}

/** The checkReferralCode callable's answer for one code, or that it could not be asked. */
export type ReferralCodeCheck = { code: string; result: 'valid' | 'invalid' | 'unchecked' };

export type ReferralCodeField = {
  state: 'empty' | 'malformed' | 'checking' | 'valid' | 'invalid' | 'unchecked';
  /** What to show under the box, if anything. */
  message: string | null;
  /** Whether Sign Up (and Google) must wait: the box holds something that is not a code that checks out. */
  blocksSignup: boolean;
  /** The code to send with the claim, once it may be sent. */
  codeToSend: string | null;
};

/**
 * The signup page's Referral code box, from what is typed and the last check.
 *
 * The box is optional, so empty is always fine. Something typed holds the
 * signup until it is a code that checks out, because an account opened with a
 * mistyped code would silently be credited to nobody (or to the wrong link):
 * the visitor can fix it or clear the box. A check that could not run (a
 * network error, the callable not deployed yet) holds nothing: the server
 * judges the code again when the account is opened.
 *
 * `checked` is matched to the code as typed now, so the answer for an earlier
 * spelling never stands for this one.
 */
export function referralCodeFieldState(raw: string, checked: ReferralCodeCheck | null): ReferralCodeField {
  if (!raw.trim()) return { state: 'empty', message: null, blocksSignup: false, codeToSend: null };
  const code = normalizeInviteCode(raw);
  if (!code) {
    return {
      state: 'malformed',
      message: 'A code is 3 to 32 letters, numbers or hyphens. Leave the box empty if you have none.',
      blocksSignup: true,
      codeToSend: null,
    };
  }
  if (!checked || checked.code !== code) return { state: 'checking', message: null, blocksSignup: true, codeToSend: null };
  if (checked.result === 'valid') return { state: 'valid', message: 'Code applied.', blocksSignup: false, codeToSend: code };
  if (checked.result === 'invalid') {
    return {
      state: 'invalid',
      message: "We don't recognise that code. Check it, or leave the box empty.",
      blocksSignup: true,
      codeToSend: null,
    };
  }
  return {
    state: 'unchecked',
    message: "We couldn't check this code right now. You can still sign up, and we'll check it then.",
    blocksSignup: false,
    codeToSend: code,
  };
}

// The Functions SDK's codes for a failure worth another try. Anything with no
// code at all is the network itself (a fetch that never got an answer).
const TRANSIENT_CODES: ReadonlySet<string> = new Set([
  'functions/unavailable',
  'functions/internal',
  'functions/deadline-exceeded',
  'functions/unknown',
  'functions/aborted',
]);

/** Whether a callable failure is worth retrying, as opposed to a refusal that will not change. */
export function isTransientCallableError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code !== 'string' || TRANSIENT_CODES.has(code);
}

/**
 * Send the claim, retrying a failure that may pass on another try.
 *
 * Resolves true once the server has answered (whether or not it credited the
 * account), false when it never did or refused outright. Never rejects: a
 * claim must not be able to break the signup it rides on.
 */
export async function claimWithRetry(
  call: (code: string) => Promise<unknown>,
  code: string,
  opts: { attempts: number; delayMs: (attempt: number) => number; sleep: (ms: number) => Promise<void> },
): Promise<boolean> {
  for (let attempt = 1; attempt <= opts.attempts; attempt++) {
    try {
      await call(code);
      return true;
    } catch (err) {
      if (!isTransientCallableError(err) || attempt === opts.attempts) return false;
      await opts.sleep(opts.delayMs(attempt));
    }
  }
  return false;
}
